/**
 * Pipeline.tsx — /#/pipeline
 *
 * Pipeline coverage · all AEs
 *
 * Track D patch (2026-05-13):
 *   - Replaced buildPortfolio() / extended.ts (fictional cast + OEM accounts) with
 *     real Supabase accounts for non-demo tenants.
 *   - Demo tenant (is_demo=true) continues to use buildPortfolio() from extended.ts.
 *   - Real tenant shows actual accounts with stage derived from account_cp_status.
 *   - Empty state shown when no accounts exist.
 *
 * v3.13 (2026-06-10):
 *   - Replaced tenant-wide Sprint window with per-account Accelerated Jumpstart.
 *   - When view='sprint': filter accounts active in Jumpstart (dayInJumpstart < 90
 *     and engagement_start_date is not null).
 *   - When view='boy': accounts where dayInJumpstart >= 90 or close_date is past
 *     the 90-day window.
 *   - When view='all': all accounts shown.
 *   - Added Day N/90 badge per account card.
 *   - Removed tenant-wide sprint window countdown from KPI block.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import DrillDownFilterBar from "@/components/DrillDownFilterBar";
import { parseDrillParams, buildDrillUrl } from "@/lib/drillUrl";
import {
  buildPortfolio,
  fmtUsd,
  type PortfolioAccount,
  type Stage,
} from "@/lib/extended";
import { supabase, getSession, getCurrentTenant } from "@/lib/session";
import { ArrowUpRight, AlertCircle, Download } from "lucide-react";
import SprintBoyToggle from "@/components/SprintBoyToggle";
import { exportRows } from "@/lib/csvExport";
import { useSprintView } from "@/lib/sprint-context";
import { computeJumpstartWindow } from "@/lib/useJumpstartWindow";

const FUNNEL_ORDER: Stage[] = [
  "Suspect",
  "Prospect",
  "Discovery",
  "Qualified Opp",
  "Proposal",
  "Negotiation",
];

// Real quarterly quota sourced from sales_plans at runtime.
// Fallback to 0 (not a fabricated number) when plans have not been entered.
// Partner tenant 2026 verified: Q4 = $1,460,000 (not the prior hardcoded $1,400,000)
// Runtime quota hook — returns 0 until sales_plans is loaded
function useQuotaByQ(tenantId: number): { loaded: boolean; q4: number; q1next: number } {
  const [quota, setQuota] = useState({ loaded: false, q4: 0, q1next: 0 });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sb = supabase();
        const currentYear = new Date().getFullYear();
        const { data } = await sb
          .from("sales_plans")
          .select("q4, q1, ae_user_id, users!inner(tenant_id)")
          .eq("users.tenant_id", tenantId)
          .eq("plan_year", currentYear);
        if (!cancelled && data) {
          const q4 = (data as any[]).reduce((s: number, r: any) => s + (Number(r.q4) || 0), 0);
          const q1next = (data as any[]).reduce((s: number, r: any) => s + (Number(r.q1) || 0), 0);
          setQuota({ loaded: true, q4, q1next });
        }
      } catch {
        if (!cancelled) setQuota({ loaded: true, q4: 0, q1next: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);
  return quota;
}

// ── Real-account adapter ─────────────────────────────────────────────────────

function stageFromCps(cpStatuses: { status: string | null }[]): Stage {
  const greens = cpStatuses.filter((c) => c.status === "green").length;
  const reds = cpStatuses.filter((c) => c.status === "red").length;
  if (reds >= 2) return "Suspect";
  if (greens >= 9) return "Negotiation";
  if (greens >= 7) return "Proposal";
  if (greens >= 5) return "Qualified Opp";
  if (greens >= 3) return "Discovery";
  if (greens >= 1) return "Prospect";
  return "Suspect";
}

export async function fetchRealPortfolio(tenantId: number): Promise<PortfolioAccount[]> {
  const sb = supabase();

  const { data: accData } = await sb
    .from("accounts")
    .select("id, name, industry, owner_user_id, aip_ext, status, engagement_start_date")
    .eq("tenant_id", tenantId);

  if (!accData || accData.length === 0) return [];

  const accIds = accData.map((a: any) => a.id);
  const ownerIds = Array.from(
    new Set((accData as any[]).map((a) => a.owner_user_id).filter(Boolean))
  );

  const [{ data: cpData }, userResp] = await Promise.all([
    sb.from("account_cp_status").select("account_id, status").in("account_id", accIds),
    ownerIds.length > 0
      ? sb.from("users").select("id, full_name, email").in("id", ownerIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);
  const userData = (userResp as any).data ?? [];

  const cpByAccount = new Map<number, { status: string | null }[]>();
  for (const cp of (cpData ?? []) as any[]) {
    if (!cpByAccount.has(cp.account_id)) cpByAccount.set(cp.account_id, []);
    cpByAccount.get(cp.account_id)!.push({ status: cp.status });
  }

  // Map owner_user_id → rep display name (full_name | email | fallback)
  const repByOwner = new Map<string, string>();
  for (const u of userData as any[]) {
    repByOwner.set(u.id, u.full_name || u.email || "Unassigned");
  }

  return accData.map((a: any): PortfolioAccount => {
    const cps = cpByAccount.get(a.id) ?? [];
    const stage = stageFromCps(cps);
    const arr = typeof a.aip_ext?.est_arr === "number" ? a.aip_ext.est_arr : Number(a.aip_ext?.est_arr) || 0;
    const repName = (a.owner_user_id && repByOwner.get(a.owner_user_id)) || "Unassigned";
    return {
      id: String(a.id),
      company: a.name,
      industry: a.industry || "—",
      rep: repName as any,
      stage,
      arr,
      amount: arr,
      closeQuarter: "Q4-26",
      pushedDays: 0,
      daysInStage: 0,
      mpact: 0,
      mpactDelta: 0,
      hasCompellingEvent: false,
      hasStakeholderMap: false,
      hasPlanInFlight: false,
      topAccount: false,
      // v3.13: per-account Jumpstart field
      engagement_start_date: a.engagement_start_date ?? null,
    } as PortfolioAccount & { engagement_start_date: string | null };
  });
}

// ── Jumpstart Day Badge ───────────────────────────────────────────────────────

function JumpstartDayBadge({ engagementStartDate }: { engagementStartDate: string | null | undefined }) {
  const jw = computeJumpstartWindow(engagementStartDate);
  if (!jw) return null;

  const label =
    jw.dayInJumpstart > 90
      ? "Post-Jumpstart"
      : `Day ${jw.dayInJumpstart}/90`;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 10,
        fontWeight: 600,
        borderRadius: 9999,
        padding: "1px 7px",
        background: jw.color + "22",
        color: jw.color,
        border: `1px solid ${jw.color}44`,
        whiteSpace: "nowrap",
        letterSpacing: "0.02em",
      }}
      title={`Accelerated Jumpstart: ${label}`}
    >
      {label}
    </span>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function Pipeline() {
  const session = getSession();
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;
  const tenantId = session?.tenantId ?? 1;
  const [, setLocation] = useLocation();

  // BUILD-14: Read drill-down filter params from URL
  const drillParams = parseDrillParams();
  const filterOwner = drillParams.owner ?? null;   // user UUID
  const filterStage = drillParams.stage ?? null;   // stage name
  const filterGap = drillParams.gap ?? null;       // '1' = show gap accounts
  const hasDrillFilter = !!(filterOwner || filterStage || filterGap);

  // Human-readable label for the back link
  const drillBackHref = drillParams.from ?? "/dashboards/manager";
  const drillBackLabel = drillParams.fromLabel ?? "Dashboard";

  const quotaData = useQuotaByQ(tenantId);
  const [portfolio, setPortfolio] = useState<PortfolioAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (isDemo) {
        setPortfolio(buildPortfolio());
        setLoading(false);
        return;
      }
      try {
        const real = await fetchRealPortfolio(tenantId);
        if (!cancelled) setPortfolio(real);
      } catch (err) {
        console.error("[Pipeline] fetch failed:", err);
        if (!cancelled) setPortfolio([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isDemo, tenantId]);

  // BUILD-14: Apply drill-down filters client-side
  const filteredPortfolio = useMemo(() => {
    let result = portfolio;
    if (filterOwner) result = result.filter((a) => (a as any).ownerUserId === filterOwner || (a as any).owner_user_id === filterOwner);
    if (filterStage) {
      // Normalize stage comparison (Pipeline stage names vs Manager stage names)
      const stageLower = filterStage.toLowerCase();
      result = result.filter((a) => a.stage.toLowerCase().includes(stageLower) || stageLower.includes(a.stage.toLowerCase()));
    }
    // gap=1: show accounts that are not in closing/won stages
    if (filterGap === "1") result = result.filter((a) => !([
      "Negotiation",
      "Closed Won",
    ] as string[]).includes(a.stage));
    return result;
  }, [portfolio, filterOwner, filterStage, filterGap]);

  const { view } = useSprintView();

  // v3.13: Per-account Jumpstart filter
  const viewFilteredPortfolio = useMemo(() => {
    if (view === "sprint") {
      // Accelerated Jumpstart: accounts with engagement_start_date and dayInJumpstart < 90
      return filteredPortfolio.filter((a) => {
        const esd = (a as any).engagement_start_date;
        if (!esd) return false;
        const jw = computeJumpstartWindow(esd);
        return jw !== null && jw.dayInJumpstart < 90;
      });
    }
    if (view === "boy") {
      // Balance of year: accounts where dayInJumpstart >= 90 or no engagement_start_date
      return filteredPortfolio.filter((a) => {
        const esd = (a as any).engagement_start_date;
        if (!esd) return true; // no start date — counts as BOY
        const jw = computeJumpstartWindow(esd);
        return jw === null || jw.dayInJumpstart >= 90;
      });
    }
    // all
    return filteredPortfolio;
  }, [filteredPortfolio, view]);

  const funnel = useMemo(() => {
    const m = new Map<Stage, { count: number; total: number }>();
    FUNNEL_ORDER.forEach((s) => m.set(s, { count: 0, total: 0 }));
    for (const a of viewFilteredPortfolio) {
      const e = m.get(a.stage);
      if (e) {
        e.count += 1;
        e.total += a.amount;
      }
    }
    return FUNNEL_ORDER.map((s) => ({ stage: s, ...m.get(s)! }));
  }, [viewFilteredPortfolio]);

  const maxAmt = Math.max(1, ...funnel.map((f) => f.total));

  const coverage = useMemo(() => {
    const out: { q: string; pipe: number; quota: number; ratio: number }[] = [];
    if (quotaData.loaded) {
      const currentQ = "Q4-26";
      const nextQ = "Q1-27";
      const q4pipe = viewFilteredPortfolio
        .filter((a) => a.closeQuarter === currentQ && a.stage !== "Suspect" && a.stage !== "Closed Lost")
        .reduce((s, a) => s + a.amount, 0);
      const q1pipe = viewFilteredPortfolio
        .filter((a) => a.closeQuarter === nextQ && a.stage !== "Suspect" && a.stage !== "Closed Lost")
        .reduce((s, a) => s + a.amount, 0);
      if (quotaData.q4 > 0) out.push({ q: currentQ, pipe: q4pipe, quota: quotaData.q4, ratio: q4pipe / quotaData.q4 });
      if (quotaData.q1next > 0) out.push({ q: nextQ, pipe: q1pipe, quota: quotaData.q1next, ratio: q1pipe / quotaData.q1next });
      if (out.length === 0) out.push({ q: "Coverage", pipe: 0, quota: 0, ratio: 0 });
    }
    return out;
  }, [viewFilteredPortfolio, quotaData]);

  // v3.13: Jumpstart pipeline totals (replaces sprint split KPIs)
  const jumpstartTotals = useMemo(() => {
    let activeCount = 0, activeTotal = 0;
    let boyCount = 0, boyTotal = 0;
    for (const a of filteredPortfolio) {
      if (a.stage === "Closed Lost") continue;
      const esd = (a as any).engagement_start_date;
      const jw = esd ? computeJumpstartWindow(esd) : null;
      const inJumpstart = jw !== null && jw.dayInJumpstart < 90;
      if (inJumpstart) {
        activeCount++;
        activeTotal += a.amount;
      } else {
        boyCount++;
        boyTotal += a.amount;
      }
    }
    return { activeCount, activeTotal, boyCount, boyTotal };
  }, [filteredPortfolio]);

  const aging = useMemo(
    () =>
      viewFilteredPortfolio
        .filter((a) => a.daysInStage > 30 && a.stage !== "Closed Won" && a.stage !== "Closed Lost")
        .sort((a, b) => b.daysInStage - a.daysInStage),
    [viewFilteredPortfolio],
  );

  const slipped = useMemo(
    () => viewFilteredPortfolio.filter((a) => a.pushedDays > 0).sort((a, b) => b.pushedDays - a.pushedDays),
    [viewFilteredPortfolio],
  );

  // BUILD-14: filter chip labels
  const filterLabels: Record<string, string | null | undefined> = {};
  if (filterOwner) filterLabels["Owner"] = filterOwner;
  if (filterStage) filterLabels["Stage"] = filterStage;
  if (filterGap === "1") filterLabels["View"] = "Gap accounts";

  function clearFilter(key: string) {
    const next = { ...drillParams };
    const paramKey = key === "Owner" ? "owner" : key === "Stage" ? "stage" : key === "View" ? "gap" : key.toLowerCase();
    delete (next as any)[paramKey];
    const remaining: Record<string, string | undefined> = {
      owner: next.owner,
      stage: next.stage,
      gap: next.gap,
      from: (next as any).from,
      fromLabel: (next as any).fromLabel,
    };
    setLocation(buildDrillUrl("/pipeline", remaining));
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "Pipeline" }]} pageTitle="Pipeline" pageMeta="Pipeline coverage · all accounts">
      {/* BUILD-14: drill-down filter breadcrumb */}
      {hasDrillFilter && (
        <DrillDownFilterBar
          backLabel={drillBackLabel}
          backHref={drillBackHref}
          filters={filterLabels}
          onClear={() => setLocation("/pipeline")}
          onRemove={clearFilter}
        />
      )}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
            Pipeline dashboard
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Coverage and motion</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Funnel by stage, quarterly coverage versus 3x target, aging deals and slippage.
            {isDemo && " · Sample data"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))] inline-flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded border border-border whitespace-nowrap transition-colors"
            onClick={() => {
              const data = (viewFilteredPortfolio || []).map(p => ({
                id: (p as any).id,
                account: (p as any).account || (p as any).name,
                owner: (p as any).owner || (p as any).ownerName,
                stage: (p as any).stage,
                arr: (p as any).arr,
                close_date: (p as any).closeDate,
                pushed_days: (p as any).pushedDays,
                verdict: (p as any).verdict,
                engagement_start_date: (p as any).engagement_start_date,
              }));
              exportRows("pipeline", data);
            }}
            title="Download visible pipeline as CSV"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <SprintBoyToggle />
        </div>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading pipeline...</div>
      )}

      {!loading && viewFilteredPortfolio.length === 0 && (
        <div className="card-shell p-8 text-center mb-6">
          <div className="text-sm text-muted-foreground">
            {hasDrillFilter
              ? "No accounts match the active filters. Adjust or clear filters to see more."
              : <>Data activates as your team logs activity.<br />Add accounts and score them with Helix to populate this view.</>}
          </div>
        </div>
      )}

      {!loading && viewFilteredPortfolio.length > 0 && (
        <>
          {(view === "sprint" || view === "all") && (
            <div className="mb-6 grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Accelerated Jumpstart pipeline{" "}
                  <span className="font-normal text-[10px] text-neutral-400">
                    (active accounts · Jumpstart Day 1–90)
                  </span>
                </div>
                {jumpstartTotals.activeCount > 0 ? (
                  <>
                    <div className="mt-2 flex items-baseline gap-3">
                      <div className="text-2xl font-semibold text-neutral-900">{fmtUsd(jumpstartTotals.activeTotal)}</div>
                      <div className="text-xs text-neutral-500">{jumpstartTotals.activeCount} accounts</div>
                    </div>
                  </>
                ) : (
                  <div className="mt-2 text-sm text-neutral-400 italic">
                    No accounts are currently in an active Jumpstart window.
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Balance of year pipeline{" "}
                  <span className="font-normal text-[10px] text-neutral-400">
                    (open deals · close date after Jumpstart window or undated)
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-3">
                  <div className="text-2xl font-semibold text-neutral-900">{fmtUsd(jumpstartTotals.boyTotal)}</div>
                  <div className="text-xs text-neutral-500">{jumpstartTotals.boyCount} accounts</div>
                </div>
              </div>
            </div>
          )}

          {/* Stage funnel */}
          <div className="card-shell p-5 mb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
              Stage funnel <span className="normal-case tracking-normal text-[10px] text-muted-foreground/70">(all open + closed deals across all stages)</span>
            </div>
            <div className="space-y-2">
              {funnel.map((f) => (
                <div
                  key={f.stage}
                  className="grid grid-cols-[110px_1fr_70px_56px] sm:grid-cols-[160px_1fr_80px_60px] items-center gap-3 text-sm"
                >
                  <div className="text-muted-foreground truncate">{f.stage}</div>
                  <div className="bar-track h-3">
                    <div
                      className="bar-fill bg-[hsl(var(--accent-red))]/70"
                      style={{ width: `${(f.total / maxAmt) * 100}%` }}
                    />
                  </div>
                  <div className="tabular text-[12px] text-foreground text-right">
                    {f.total > 0 ? fmtUsd(f.total) : "—"}
                  </div>
                  <div className="tabular text-xs text-muted-foreground text-right">
                    {f.count} {f.count === 1 ? "deal" : "deals"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Coverage by quarter */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {coverage.map((c) => (
              <CoverageCard key={c.q} q={c.q} pipe={c.pipe} quota={c.quota} ratio={c.ratio} />
            ))}
          </div>

          {/* Account list with Jumpstart day badges */}
          <div className="card-shell p-5 mb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
              Accounts{" "}
              <span className="normal-case tracking-normal text-[10px] text-muted-foreground/70">
                · Day N/90 shows position in Accelerated Jumpstart
              </span>
            </div>
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-2 py-2 font-medium">Account</th>
                    <th className="text-left px-2 py-2 font-medium">Stage</th>
                    <th className="text-left px-2 py-2 font-medium">Rep</th>
                    <th className="text-right px-2 py-2 font-medium">Amount</th>
                    <th className="text-right px-2 py-2 font-medium">Jumpstart</th>
                  </tr>
                </thead>
                <tbody>
                  {viewFilteredPortfolio.slice(0, 50).map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0 hover:bg-[hsl(var(--surface-2))] transition-colors">
                      <td className="px-2 py-3">
                        <Link href={`/account/${a.id}`} className="text-foreground hover:underline inline-flex items-center gap-1">
                          {a.company} <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      </td>
                      <td className="px-2 py-3 text-muted-foreground">{a.stage}</td>
                      <td className="px-2 py-3 text-muted-foreground">{a.rep}</td>
                      <td className="px-2 py-3 text-right tabular text-muted-foreground">{fmtUsd(a.amount)}</td>
                      <td className="px-2 py-3 text-right">
                        <JumpstartDayBadge engagementStartDate={(a as any).engagement_start_date} />
                      </td>
                    </tr>
                  ))}
                  {viewFilteredPortfolio.length === 0 && (
                    <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-muted-foreground">No accounts in this view.</td></tr>
                  )}
                </tbody>
              </table>
              {viewFilteredPortfolio.length > 50 && (
                <div className="mt-2 text-xs text-muted-foreground text-center">
                  Showing 50 of {viewFilteredPortfolio.length} accounts
                </div>
              )}
            </div>
          </div>

          {/* Aging report */}
          <div className="card-shell p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  Aging report
                </div>
                <div className="text-[15px] font-medium tracking-tight mt-0.5">
                  Deals stuck more than 30 days in stage
                </div>
              </div>
              <span className="tabular text-xs text-muted-foreground">{aging.length} deals</span>
            </div>
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-2 py-2 font-medium">Account</th>
                    <th className="text-left px-2 py-2 font-medium">Stage</th>
                    <th className="text-left px-2 py-2 font-medium">Rep</th>
                    <th className="text-right px-2 py-2 font-medium">Amount</th>
                    <th className="text-right px-2 py-2 font-medium">Days in stage</th>
                  </tr>
                </thead>
                <tbody>
                  {aging.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0 hover:bg-[hsl(var(--surface-2))] transition-colors">
                      <td className="px-2 py-3">
                        <Link href={`/account/${a.id}`} className="text-foreground hover:underline inline-flex items-center gap-1">
                          {a.company} <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      </td>
                      <td className="px-2 py-3 text-muted-foreground">{a.stage}</td>
                      <td className="px-2 py-3 text-muted-foreground">{a.rep}</td>
                      <td className="px-2 py-3 text-right tabular text-muted-foreground">{fmtUsd(a.amount)}</td>
                      <td className="px-2 py-3 text-right tabular">
                        <span className={a.daysInStage >= 40 ? "text-rose-300" : "text-orange-300"}>
                          {a.daysInStage}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {aging.length === 0 && (
                    <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-muted-foreground">No aging deals.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Slippage */}
          <div className="card-shell p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  Slippage
                </div>
                <div className="text-[15px] font-medium tracking-tight mt-0.5">
                  Deals that pushed close date
                </div>
              </div>
              <span className="tabular text-xs text-muted-foreground">{slipped.length} deals</span>
            </div>
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-2 py-2 font-medium">Account</th>
                    <th className="text-left px-2 py-2 font-medium">Rep</th>
                    <th className="text-right px-2 py-2 font-medium">Amount</th>
                    <th className="text-right px-2 py-2 font-medium">Pushed</th>
                    <th className="text-left px-2 py-2 font-medium">Now closing</th>
                  </tr>
                </thead>
                <tbody>
                  {slipped.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0 hover:bg-[hsl(var(--surface-2))] transition-colors">
                      <td className="px-2 py-3">
                        <Link href={`/account/${a.id}`} className="text-foreground hover:underline inline-flex items-center gap-1">
                          {a.company} <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      </td>
                      <td className="px-2 py-3 text-muted-foreground">{a.rep}</td>
                      <td className="px-2 py-3 text-right tabular text-muted-foreground">{fmtUsd(a.amount)}</td>
                      <td className="px-2 py-3 text-right tabular text-rose-300">+{a.pushedDays}d</td>
                      <td className="px-2 py-3 text-muted-foreground">{a.closeQuarter}</td>
                    </tr>
                  ))}
                  {slipped.length === 0 && (
                    <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-muted-foreground">No slippage this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </OperatorShell>
  );
}

function CoverageCard({
  q,
  pipe,
  quota,
  ratio,
}: {
  q: string;
  pipe: number;
  quota: number;
  ratio: number;
}) {
  const target = 3;
  const pct = Math.min(100, (ratio / target) * 100);
  const color = ratio >= 3 ? "bg-emerald-400" : ratio >= 2.2 ? "bg-orange-400" : "bg-rose-400";
  const tone = ratio >= 3 ? "text-emerald-300" : ratio >= 2.2 ? "text-orange-300" : "text-rose-300";
  return (
    <div className="card-shell p-5">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{q}</div>
        {ratio < 3 && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-rose-300">
            <AlertCircle className="w-3 h-3" /> Below 3x
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span className={`tabular text-3xl font-semibold ${tone}`}>{ratio.toFixed(2)}x</span>
        <span className="text-xs text-muted-foreground">target 3.00x</span>
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">
        {fmtUsd(pipe)} qualified pipeline · {fmtUsd(quota)} quota
      </div>
      <div className="mt-3 bar-track h-2">
        <div className={`bar-fill ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
